import type {
  BulkAction,
  BulkResult,
  ComposeRequest,
  ComposeResponse,
  RebalanceResult,
  RunResult,
  RunSummary,
  Schedule,
  ScheduleInput,
  ScheduleListResponse,
  TargetCatalog,
  TellrawLibraryEntry,
  TellrawLibraryInput,
} from '../types/scheduledCommands';

/**
 * Scheduled commands and the tellraw library.
 *
 * Argus never talks to Mr. Roboto directly. argus-backend verifies the signed
 * Access session, checks `argus.scheduledcommands.*`, and only then forwards to
 * the bot with its token, so a browser never holds a credential that can run
 * console commands. The library lives in Argus's own D1.
 */
const API_BASE_URL = 'https://spy.playcdu.co/api/notes';

/** Reads and ordinary writes. The bot answers these from SQLite in milliseconds. */
const DEFAULT_TIMEOUT_MS = 20_000;
/** A run waits out Quasar's five-second feedback window and any RCON fallbacks. */
const RUN_TIMEOUT_MS = 45_000;
/** One assistant turn, which is a model call behind the bot. */
const COMPOSE_TIMEOUT_MS = 70_000;

const FALLBACK: Record<number, string> = {
  400: 'That was not accepted. Check the highlighted fields.',
  401: 'Your Argus session has expired. Reload the page to sign in again.',
  403: 'You do not have permission to do that.',
  404: 'That no longer exists. It may have been deleted by someone else.',
  413: 'That is too large to send.',
  429: 'Too many requests at once. Wait a moment and try again.',
  502: 'Mr. Roboto gave an answer Argus could not use.',
  503: 'Mr. Roboto cannot be reached right now.',
  504: 'Mr. Roboto took too long to answer.',
};

export class ScheduledCommandsApiError extends Error {
  readonly status: number;
  /** The field the backend blamed, when it named one. */
  readonly field: string | null;

  constructor(status: number, message: string, field: string | null = null) {
    super(message);
    this.name = 'ScheduledCommandsApiError';
    this.status = status;
    this.field = field;
  }
}

async function readError(response: Response): Promise<ScheduledCommandsApiError> {
  const fallback = FALLBACK[response.status] ?? `Request failed (${response.status}).`;
  try {
    const body = (await response.json()) as { error?: unknown; field?: unknown };
    const message = typeof body.error === 'string' && body.error.trim() ? body.error : fallback;
    const field = typeof body.field === 'string' && body.field ? body.field : null;
    return new ScheduledCommandsApiError(response.status, message, field);
  } catch {
    return new ScheduledCommandsApiError(response.status, fallback);
  }
}

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    });
    if (!response.ok) throw await readError(response);
    // Access answers an expired session with a redirect to its login page,
    // which arrives here as a 200 full of HTML rather than as an error.
    if (response.redirected || !response.headers.get('content-type')?.includes('application/json')) {
      throw new ScheduledCommandsApiError(401, FALLBACK[401]);
    }
    return (await response.json()) as T;
  } catch (error) {
    if (timedOut) throw new ScheduledCommandsApiError(504, FALLBACK[504]);
    if (error instanceof ScheduledCommandsApiError) throw error;
    throw new ScheduledCommandsApiError(503, 'Argus could not be reached. Check your connection and try again.');
  } finally {
    clearTimeout(timer);
  }
}

const json = (body: unknown, method: string): RequestInit => ({ method, body: JSON.stringify(body) });

const scheduleId = (id: number) => `/scheduled-commands/${encodeURIComponent(String(id))}`;

class ScheduledCommandsApiService {
  list(): Promise<ScheduleListResponse> {
    return request('/scheduled-commands');
  }

  targets(): Promise<TargetCatalog> {
    return request('/scheduled-commands/targets');
  }

  async create(input: ScheduleInput): Promise<Schedule> {
    const { schedule } = await request<{ schedule: Schedule }>('/scheduled-commands', json(input, 'POST'));
    return schedule;
  }

  /** Send only what changed: a patch that leaves the interval alone does not move the schedule's slot. */
  async update(id: number, patch: Partial<ScheduleInput>): Promise<Schedule> {
    const { schedule } = await request<{ schedule: Schedule }>(scheduleId(id), json(patch, 'PATCH'));
    return schedule;
  }

  async remove(id: number): Promise<void> {
    await request<{ deleted: number }>(scheduleId(id), { method: 'DELETE' });
  }

  bulk(ids: number[], action: BulkAction): Promise<BulkResult> {
    return request('/scheduled-commands/bulk', json({ ids, action }, 'POST'));
  }

  /** Run now on the schedule's targets, or, with a player, only on the server that player is on. */
  run(id: number, player?: string): Promise<RunResult> {
    return request(`${scheduleId(id)}/run`, json(player ? { player } : {}, 'POST'), RUN_TIMEOUT_MS);
  }

  async runs(id: number, limit = 50): Promise<RunSummary[]> {
    const { runs } = await request<{ runs: RunSummary[] }>(`${scheduleId(id)}/runs?limit=${limit}`);
    return runs;
  }

  rebalance(): Promise<RebalanceResult> {
    return request('/scheduled-commands/rebalance', json({}, 'POST'));
  }

  /** Sends one tellraw to one player, wherever they are. Nothing else can go through this. */
  async test(player: string, component: unknown): Promise<{ run: RunSummary; responded: string[] }> {
    const result = await request<{ run: RunSummary; responded?: string[] }>(
      '/scheduled-commands/test',
      json({ player, component }, 'POST'),
      RUN_TIMEOUT_MS,
    );
    return { run: result.run, responded: result.responded ?? [] };
  }

  compose(body: ComposeRequest): Promise<ComposeResponse> {
    return request('/scheduled-commands/compose', json(body, 'POST'), COMPOSE_TIMEOUT_MS);
  }

  async library(): Promise<TellrawLibraryEntry[]> {
    const { entries } = await request<{ entries: TellrawLibraryEntry[] }>('/tellraw-library');
    return entries;
  }

  async libraryEntry(id: string): Promise<TellrawLibraryEntry> {
    const { entry } = await request<{ entry: TellrawLibraryEntry }>(`/tellraw-library/${encodeURIComponent(id)}`);
    return entry;
  }

  async saveToLibrary(input: TellrawLibraryInput): Promise<TellrawLibraryEntry> {
    const { entry } = await request<{ entry: TellrawLibraryEntry }>('/tellraw-library', json(input, 'POST'));
    return entry;
  }

  async updateLibraryEntry(id: string, patch: Partial<TellrawLibraryInput>): Promise<TellrawLibraryEntry> {
    const { entry } = await request<{ entry: TellrawLibraryEntry }>(
      `/tellraw-library/${encodeURIComponent(id)}`,
      json(patch, 'PATCH'),
    );
    return entry;
  }

  async deleteLibraryEntry(id: string): Promise<void> {
    await request<{ deleted: string }>(`/tellraw-library/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

export const scheduledCommandsApi = new ScheduledCommandsApiService();
